//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Palette, darken, lighten } from '@mui/material';
import { DeepPartial } from '@/redux/slices/slice_types';
import { AnimationSpec } from 'chart.js';

// the max height of all charts (px)
export const chartMaxHeight = 300;

// number of cards to show per row
export const sashMaximumSize = 2;

export type ChartColor = {
  start: string; // gradient start color
  end: string; // gradient end color
  hover: {
    dark: string; // solid color on dark mode's hover
    light: string; // solid color on light mode's hover
  };
};

const paletteKeysToKeep = [
  'default',
  'warning',
  'pending',
  'info',
  'primary',
  'secondary',
  'tertiary',
  'error',
  'lavender',
  'success',
  'teal'
] as const;
export type ExtractedPaletteKeys = (typeof paletteKeysToKeep)[number]; // Extract the string literals from the array

export const defaultChartColors = paletteKeysToKeep; // order for default colors used in chart

export type ColorMap = Record<keyof Extract<Palette, ExtractedPaletteKeys>, ChartColor>;

export const generateChartColorMap = (palette: Palette): ColorMap => {
  const colorMap: ColorMap = {};

  for (const colorToKeep of paletteKeysToKeep) {
    const colorToUse = palette[colorToKeep as keyof Palette]
      ? palette[colorToKeep as keyof Palette].main
      : colorToKeep;
    if (colorToUse) colorMap[colorToKeep] = createChartColorFromHex(colorToUse);
  }

  return colorMap;
};

export const createChartColorFromHex = (color: string) => {
  return {
    start: color,
    end: lighten(color, 0.4),
    hover: {
      light: lighten(color, 0.5),
      dark: darken(color, 0.2)
    }
  };
};

export const defaultChartAnimation: DeepPartial<AnimationSpec<any>> = {
  duration: 1000,
  easing: 'easeInOutQuad'
};
